import React, { useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Link,
  mergeClasses,
  Text,
  Title1,
  tokens,
} from '@fluentui/react-components';
import { ArrowLeftRegular } from '@fluentui/react-icons';

import { useUserInvitationSelectionPageStyles } from './use-user-invitation-selection-page-styles';
import { IdpButtonList, type IdpButtonListProps } from '../idp-button-list';

// The "Create an account" (create) variant only offers Microsoft and Google — Facebook
// sign-up is not supported for new-account creation in the invitation flow. The "Use an
// existing account" (signin) variant keeps Facebook so returning users can sign back in.
const CREATE_PROVIDERS: IdpButtonListProps['providers'] = ['MSA', 'Google'];
const SIGNIN_PROVIDERS: IdpButtonListProps['providers'] = ['MSA', 'Google', 'Facebook'];

export type InvitationIdpView = 'create' | 'signin';

export type InvitationIdpViewProps = {
  variant: InvitationIdpView;
  daysLeftPill?: React.ReactNode;
  onBack: () => void;
  onCreate: () => void;
  onSignIn: () => void;
  logger?: {
    // eslint-disable-next-line no-unused-vars
    trace: (message: string) => void;
  };
  // eslint-disable-next-line no-unused-vars
  onIdpSelected?: (provider: string) => void;
};

// IDP selection view. 'create' and 'signin' differ only in copy; the IDP redirect
// behavior is identical.
export const InvitationIdpSelectionView: React.FC<InvitationIdpViewProps> = ({
  variant, daysLeftPill, onBack, onCreate, onSignIn, logger, onIdpSelected,
}) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useUserInvitationSelectionPageStyles();
  const isCreate = variant === 'create';

  return (
    <div className={classes.textAndButtons}>
      <div className={classes.invitationMeta}>
        <Link
          className={mergeClasses('invitation-back-link', classes.invitationBackLink)}
          onClick={onBack}
        >
          <ArrowLeftRegular className={classes.invitationBackIcon} aria-hidden />
          {i18n.getString(_TL_('Back'))}
        </Link>
        {daysLeftPill}
      </div>
      <Title1 as="h1" block className={mergeClasses('idp-title', classes.title)}>
        {isCreate
          ? i18n.getString(_TL_('Create an account'))
          : i18n.getString(_TL_('Use your existing account'))}
      </Title1>
      <Text block size={400} color={tokens.colorNeutralForeground2}>
        {isCreate
          ? i18n.getString(_TL_('Choose how you\'d like to always sign in to Microsoft Advertising.'))
          : i18n.getString(_TL_('Sign back in with the same account you last used.'))}
      </Text>
      <div className={mergeClasses('idp-selection-page-buttons', classes.buttons)}>
        <IdpButtonList
          scenario="userInvitation"
          invitationVariant={variant}
          logger={logger}
          onIdpSelected={onIdpSelected}
          providers={isCreate ? CREATE_PROVIDERS : SIGNIN_PROVIDERS}
        />
      </div>
      <div className={classes.crossLink}>
        {isCreate ? (
          <>
            <Text weight="semibold">{i18n.getString(_TL_('I already use Microsoft Advertising.'))}</Text>
            {' '}
            <Link className="invitation-sign-in-link" onClick={onSignIn}>
              {i18n.getString(_TL_('Use my existing account'))}
            </Link>
          </>
        ) : (
          <>
            <Text weight="semibold">{i18n.getString(_TL_('I\'m new to Microsoft Advertising.'))}</Text>
            {' '}
            <Link className="invitation-create-account-link" onClick={onCreate}>
              {i18n.getString(_TL_('Create an account'))}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};
